import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';
import { useAuthenticator } from '@aws-amplify/ui-react';

// Define the Zod schema for work estimate
const WorkEstimateSchema = z.object({
  value: z.coerce.number().positive('Estimate must be a positive number'),
  reason: z.string().optional(),
});

// Define the form values type based on the schema
type WorkEstimateFormValues = z.infer<typeof WorkEstimateSchema>;

interface WorkEstimateFormProps {
  taskId: string;
  estimate?: wasm.WorkEstimate;
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onCancel?: () => void;
}

const WorkEstimateForm: React.FC<WorkEstimateFormProps> = ({
  taskId,
  estimate,
  onSubmit,
  onCancel
}) => {
  const { user } = useAuthenticator(ctx => [ctx.user]);
  const userId = user?.userId || 'anonymous';

  const initialValues: WorkEstimateFormValues = {
    value: estimate?.value || 0,
    reason: estimate?.annotation?.reason || '',
  };

  const handleSubmit = async (values: WorkEstimateFormValues) => {
    // Create a WorkEstimate object
    const workEstimate: wasm.WorkEstimate = {
      id: estimate?.id || uuidv4(),
      value: values.value,
      annotation: values.reason ? {
        reason: values.reason,
        updated_at: new Date().toISOString(),
        creator: {
          type: "User",
          id: userId,
        },
      } : null,
    };

    // Create the command
    const command: wasm.DatabaseCommand = {
      type: "UpdateTask",
      id: taskId,
      actions: [{
        type: "InsertEstimate",
        estimate: workEstimate
      }]
    };

    await onSubmit(command);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={toFormikValidationSchema(WorkEstimateSchema)}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, errors, touched }) => (
        <Form>
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <Label htmlFor="value" className="mb-2 block md:text-sm">Estimate Value</Label>
              <Field
                as={TextInput}
                id="value"
                name="value"
                type="number"
                placeholder="Enter value"
                color={errors.value && touched.value ? 'failure' : undefined}
                helperText={<ErrorMessage name="value" component="span" className="text-red-500" />}
                sizing="sm"
              />
            </div>

            <div className="flex-grow">
              <Label htmlFor="reason" className="mb-2 block md:text-sm">Reason (optional)</Label>
              <Field
                as={TextInput}
                id="reason"
                name="reason"
                type="text"
                placeholder="Explain your estimate"
                sizing="sm"
              />
            </div>

            <div className="flex space-x-2 mt-2 md:mt-0">
              {onCancel && (
                <Button color="light" onClick={onCancel} size="sm">
                  Cancel
                </Button>
              )}
              <Button type="submit" disabled={isSubmitting} size="sm">
                {estimate ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default WorkEstimateForm;
